import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  UserIcon,
  PhoneIcon,
  MailIcon,
  TruckIcon,
  CalendarIcon,
  MessageSquareIcon,
  PencilLineIcon,
  Trash2Icon,
  PlusIcon,
  ArrowLeftIcon,
  SparklesIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

interface Note {
  id: number
  content: string
  created_at: string
}

interface Lead {
  id: number
  name: string
  phone: string | null
  email: string | null
  source: string
  source_id: string | null
  instagram_username: string | null
  messenger_username: string | null
  vehicle_type: string | null
  issue_description: string | null
  feedback: string | null
  urgency: string
  sentiment: string | null
  quality: string | null
  status: string
  notes: Note[]
  created_at: string
  updated_at: string
}

interface LeadShowProps {
  lead: Lead
}

export default function LeadShow({ lead }: LeadShowProps) {
  const [newNote, setNewNote] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false)
  const [appointmentDate, setAppointmentDate] = useState('')
  const [appointmentTime, setAppointmentTime] = useState('')
  const [appointmentType, setAppointmentType] = useState('Diagnostic')

  const handleAddNote = () => {
    if (newNote.trim()) {
      router.post(`/leads/${lead.id}/add_note`, { content: newNote }, {
        preserveScroll: true,
        onSuccess: () => setNewNote('')
      })
    }
  }

  const handleStatusChange = (newStatus: string) => {
    // If changing to "Appointment Scheduled", show appointment dialog
    if (newStatus === 'Appointment Scheduled') {
      setAppointmentDialogOpen(true)
    } else {
      // Transform Title Case to snake_case for backend
      const transformedStatus = newStatus.toLowerCase().replace(/ /g, '_')
      router.patch(`/leads/${lead.id}/update_status`, { status: transformedStatus }, {
        preserveScroll: true,
      })
    }
  }

  const handleCreateAppointment = () => {
    if (appointmentDate && appointmentTime) {
      // In Level 1, we simulate creating appointment and updating lead status
      router.patch(`/leads/${lead.id}/update_status`, {
        status: 'Appointment Scheduled',
        appointment: {
          date: appointmentDate,
          time: appointmentTime,
          type: appointmentType,
        }
      }, {
        onSuccess: () => {
          setAppointmentDialogOpen(false)
          setAppointmentDate('')
          setAppointmentTime('')
          setAppointmentType('Diagnostic')
        }
      })
    }
  }

  const handleDelete = () => {
    router.delete(`/leads/${lead.id}`)
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'Emergency':
        return 'bg-red-600 hover:bg-red-700'
      case 'High':
        return 'bg-orange-600 hover:bg-orange-700'
      case 'Medium':
        return 'bg-yellow-600 hover:bg-yellow-700'
      case 'Low':
        return 'bg-green-600 hover:bg-green-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
        return 'bg-blue-600 hover:bg-blue-700'
      case 'Contacted':
        return 'bg-purple-600 hover:bg-purple-700'
      case 'Estimate Requested':
        return 'bg-indigo-600 hover:bg-indigo-700'
      case 'Appointment Scheduled':
        return 'bg-cyan-600 hover:bg-cyan-700'
      case 'Won':
        return 'bg-green-600 hover:bg-green-700'
      case 'Lost':
        return 'bg-gray-600 hover:bg-gray-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  return (
    <>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between px-4 lg:px-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => router.visit('/leads')}
                  data-testid="back-to-leads-button"
                >
                  <ArrowLeftIcon className="size-4" />
                </Button>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">{lead.name}</h1>
                  <p className="text-muted-foreground">Lead Details</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => router.post(`/leads/${lead.id}/analyze_conversation`)}
                  data-testid="analyze-conversation-button"
                >
                  <SparklesIcon className="mr-2 size-4" />
                  Analyze Conversation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => router.visit(`/leads/${lead.id}/edit`)}
                  data-testid="edit-lead-button"
                >
                  <PencilLineIcon className="mr-2 size-4" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-lead-button"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2Icon className="mr-2 size-4" />
                  Delete
                </Button>
              </div>
            </div>

            <div className="grid gap-6 px-4 lg:px-6 lg:grid-cols-3">
              {/* Main Information */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Lead Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Status and Badges */}
                    <div className="flex flex-wrap gap-2">
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status}
                      </Badge>
                      <Badge className={getUrgencyColor(lead.urgency)}>
                        {lead.urgency}
                      </Badge>
                      {lead.sentiment && (
                        <Badge
                          variant={
                            lead.sentiment === 'positive' ? 'default' :
                            lead.sentiment === 'negative' ? 'destructive' :
                            'secondary'
                          }
                          className="capitalize"
                        >
                          Sentiment: {lead.sentiment}
                        </Badge>
                      )}
                      <Badge
                        variant={
                          lead.quality === 'Good' ? 'default' :
                          lead.quality === 'Bad' ? 'destructive' :
                          'secondary'
                        }
                      >
                        Quality: {lead.quality || 'Neutral'}
                      </Badge>
                      <Badge variant="outline">{lead.source}</Badge>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <UserIcon className="size-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Name</p>
                          <p className="font-medium">{lead.name}</p>
                        </div>
                      </div>

                      {/* Social Media ID */}
                      {(() => {
                        let socialMediaId = null
                        if (lead.source === 'Instagram' && lead.instagram_username) {
                          socialMediaId = `@${lead.instagram_username}`
                        } else if (lead.source === 'Facebook Messenger' && lead.messenger_username) {
                          socialMediaId = `@${lead.messenger_username}`
                        } else if (['Telegram', 'WhatsApp', 'YouTube'].includes(lead.source) && lead.source_id) {
                          socialMediaId = `@${lead.source_id}`
                        }

                        return socialMediaId ? (
                          <div className="flex items-center gap-3">
                            <PhoneIcon className="size-5 text-muted-foreground" />
                            <div>
                              <p className="text-sm text-muted-foreground">Social Media ID</p>
                              <p className="font-medium">{socialMediaId}</p>
                            </div>
                          </div>
                        ) : null
                      })()}

                      {/* Phone */}
                      {lead.phone && (
                        <div className="flex items-center gap-3">
                          <PhoneIcon className="size-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Phone</p>
                            <p className="font-medium">{lead.phone.replace(/[^0-9]/g, '')}</p>
                          </div>
                        </div>
                      )}

                      {lead.email && (
                        <div className="flex items-center gap-3">
                          <MailIcon className="size-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Email</p>
                            <p className="font-medium">{lead.email}</p>
                          </div>
                        </div>
                      )}

                      {lead.vehicle_type && (
                        <div className="flex items-center gap-3">
                          <TruckIcon className="size-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Vehicle Type</p>
                            <p className="font-medium">{lead.vehicle_type}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Issue Description */}
                    {lead.issue_description && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Issue Description</p>
                        <div className="rounded-md bg-muted p-4">
                          <p>{lead.issue_description}</p>
                        </div>
                      </div>
                    )}

                    {/* Feedback */}
                    {lead.feedback && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Feedback</p>
                        <div className="rounded-md bg-muted p-4">
                          <p>{lead.feedback}</p>
                        </div>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <CalendarIcon className="size-4" />
                      <span>Created {new Date(lead.created_at).toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Notes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquareIcon className="size-5" />
                      Notes ({lead.notes.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add Note */}
                    <div className="space-y-2">
                      <Label htmlFor="new-note">Add Note</Label>
                      <Textarea
                        id="new-note"
                        placeholder="Add a note about this lead..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={3}
                        data-testid="add-note-textarea"
                      />
                      <Button
                        size="sm"
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="add-note-button"
                      >
                        <PlusIcon className="mr-2 size-4" />
                        Add Note
                      </Button>
                    </div>

                    {/* Notes List */}
                    {lead.notes.length > 0 ? (
                      <div className="space-y-3">
                        {lead.notes.map((note) => (
                          <div
                            key={note.id}
                            className="rounded-md border p-3 space-y-1"
                          >
                            <p className="text-sm">{note.content}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(note.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No notes yet. Add your first note above.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar - Status Management */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Change Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Label htmlFor="status-change">Update Lead Status</Label>
                    <Select
                      value={lead.status}
                      onValueChange={handleStatusChange}
                    >
                      <SelectTrigger id="status-change" data-testid="status-change-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New">New</SelectItem>
                        <SelectItem value="Contacted">Contacted</SelectItem>
                        <SelectItem value="Estimate Requested">Estimate Requested</SelectItem>
                        <SelectItem value="Appointment Scheduled">Appointment Scheduled</SelectItem>
                        <SelectItem value="Won">Won</SelectItem>
                        <SelectItem value="Lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Selecting "Appointment Scheduled" will prompt you to create an appointment
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Quick-Create Dialog */}
      <Dialog open={appointmentDialogOpen} onOpenChange={setAppointmentDialogOpen}>
        <DialogContent data-testid="appointment-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Appointment</DialogTitle>
            <DialogDescription>
              Create an appointment for {lead.name}. This will update the lead status to "Appointment Scheduled".
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="appointment-date">Date *</Label>
              <Input
                id="appointment-date"
                type="date"
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
                data-testid="appointment-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointment-time">Time *</Label>
              <Input
                id="appointment-time"
                type="time"
                value={appointmentTime}
                onChange={(e) => setAppointmentTime(e.target.value)}
                data-testid="appointment-time-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointment-type">Appointment Type *</Label>
              <Select value={appointmentType} onValueChange={setAppointmentType}>
                <SelectTrigger id="appointment-type" data-testid="appointment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                  <SelectItem value="Repair">Repair</SelectItem>
                  <SelectItem value="Inspection">Inspection</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAppointmentDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAppointment}
              disabled={!appointmentDate || !appointmentTime}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="create-appointment-button"
            >
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Lead"
        description={`Are you sure you want to delete ${lead.name}? This will permanently remove the lead and all associated notes.`}
      />
    </>
  )
}
